import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignCategoriesDialog from "@saleor/components/AssignCategoryDialog";
import AssignCollectionDialog from "@saleor/components/AssignCollectionDialog";
import AssignLanguageDialog from "@saleor/components/AssignLanguageDialog/AssignLanguageDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import AssignVariantDialog from "@saleor/components/AssignVariantDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { commonMessages, sectionNames } from "@saleor/intl";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useLanguageSearch from "@saleor/searches/useLanguageSearch";
import useProductSearch from "@saleor/searches/useProductSearch";
import useProductVariantSearch from "@saleor/searches/useProductVariantSearch";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { categoryUrl } from "../../categories/urls";
import { collectionUrl } from "../../collections/urls";
import { decimal, joinDateTime, maybe } from "../../misc";
import { productUrl, productVariantEditUrl } from "../../products/urls";
import {
  DiscountValueTypeEnum,
  VoucherTypeEnum
} from "../../types/globalTypes";
import DiscountCountrySelectDialog from "../components/DiscountCountrySelectDialog";
import VoucherDetailsPage, {
  VoucherDetailsPageFormData,
  VoucherDetailsPageTab
} from "../components/VoucherDetailsPage";
import {
  TypedVoucherCataloguesAdd,
  TypedVoucherCataloguesRemove,
  TypedVoucherCataloguesReverseElection,
  TypedVoucherDelete,
  TypedVoucherUpdate,
  useVoucherLanguagesAddMutation,
  useVoucherLanguagesRemoveMutation
} from "../mutations";
import { TypedVoucherDetails } from "../queries";
import { RequirementsPicker } from "../types";
import { VoucherCataloguesAdd } from "../types/VoucherCataloguesAdd";
import { VoucherCataloguesRemove } from "../types/VoucherCataloguesRemove";
import { VoucherCataloguesReverseElection } from "../types/VoucherCataloguesReverseElection";
import { VoucherDelete } from "../types/VoucherDelete";
import { VoucherUpdate } from "../types/VoucherUpdate";
import {
  voucherListUrl,
  voucherUrl,
  VoucherUrlDialog,
  VoucherUrlQueryParams
} from "../urls";

interface VoucherDetailsProps {
  id: string;
  params: VoucherUrlQueryParams;
}

export const VoucherDetails: React.FC<VoucherDetailsProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreLanguages,
    search: searchLanguages,
    result: searchLanguagesOpts
  } = useLanguageSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProductVariants,
    search: searchProductVariants,
    result: searchProductVariantsOpts
  } = useProductVariantSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const changeTab = (tab: VoucherDetailsPageTab) => {
    reset();
    navigate(
      voucherUrl(id, {
        activeTab: tab
      })
    );
  };

  const handleVoucherDelete = (data: VoucherDelete) => {
    if (data.voucherDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Deleted voucher"
        })
      });
      navigate(voucherListUrl(), true);
    }
  };

  const handleVoucherUpdate = (data: VoucherUpdate) => {
    if (data.voucherUpdate.errors.length === 0) {
      closeModal();
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    VoucherUrlDialog,
    VoucherUrlQueryParams
  >(navigate, params => voucherUrl(id, params), params);

  const handleCatalogueAdd = (data: VoucherCataloguesAdd) => {
    if (data.voucherCataloguesAdd.errors.length === 0) {
      closeModal();
    }
  };

  const handleCatalogueReverseElection = (
    data: VoucherCataloguesReverseElection
  ) => {
    if (data.voucherCataloguesReverseElection.errors.length === 0) {
      closeModal();
    }
  };

  const handleCatalogueRemove = (data: VoucherCataloguesRemove) => {
    if (data.voucherCataloguesRemove.errors.length === 0) {
      closeModal();
      reset();
    }
  };

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  return (
    <TypedVoucherCataloguesRemove onCompleted={handleCatalogueRemove}>
      {(voucherCataloguesRemove, voucherCataloguesRemoveOpts) => (
        <TypedVoucherCataloguesAdd onCompleted={handleCatalogueAdd}>
          {(voucherCataloguesAdd, voucherCataloguesAddOpts) => (
            <TypedVoucherCataloguesReverseElection
              onCompleted={handleCatalogueReverseElection}
            >
              {(
                voucherCataloguesReverseElection,
                voucherCataloguesReverseElectionOpts
              ) => (
                <TypedVoucherUpdate onCompleted={handleVoucherUpdate}>
                  {(voucherUpdate, voucherUpdateOpts) => (
                    <TypedVoucherDelete onCompleted={handleVoucherDelete}>
                      {(voucherDelete, voucherDeleteOpts) => (
                        <TypedVoucherDetails
                          displayLoader
                          variables={{ id, ...paginationState }}
                        >
                          {({ data, loading }) => {
                            const tabPageInfo =
                              params.activeTab ===
                              VoucherDetailsPageTab.categories
                                ? maybe(() => data.voucher.categories.pageInfo)
                                : params.activeTab ===
                                  VoucherDetailsPageTab.collections
                                ? maybe(() => data.voucher.collections.pageInfo)
                                : params.activeTab ===
                                  VoucherDetailsPageTab.products
                                ? maybe(() => data.voucher.products.pageInfo)
                                : maybe(() => data.voucher.variants.pageInfo);
                            const handleExcludeCategoriesUnassign = (
                              ids: string[]
                            ) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    categories: ids,
                                    exclude: true
                                  }
                                }
                              });
                            const handleCategoriesUnassign = (ids: string[]) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    categories: ids
                                  }
                                }
                              });
                            const handleExcludeCollectionsUnassign = (
                              ids: string[]
                            ) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    collections: ids,
                                    exclude: true
                                  }
                                }
                              });
                            const handleCollectionsUnassign = (ids: string[]) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    collections: ids
                                  }
                                }
                              });
                            const handleExcludeProductsUnassign = (
                              ids: string[]
                            ) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    products: ids,
                                    exclude: true
                                  }
                                }
                              });
                            const handleProductsUnassign = (ids: string[]) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    products: ids
                                  }
                                }
                              });
                            const handleExcludeVariantsUnassign = (
                              ids: string[]
                            ) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    variants: ids,
                                    exclude: true
                                  }
                                }
                              });
                            const handleVariantsUnassign = (ids: string[]) =>
                              voucherCataloguesRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    variants: ids
                                  }
                                }
                              });

                            const handleSubmit = async (
                              data: VoucherDetailsPageFormData
                            ) => {
                              const result = await voucherUpdate({
                                variables: {
                                  id,
                                  input: {
                                    name: data.name,
                                    limitTime: data.hasLimitTime
                                      ? data.limitTime
                                      : 0,
                                    applyNumberPerCustomer: data.hasApplyNumberPerCustomer
                                      ? parseInt(
                                          data.applyNumberPerCustomer,
                                          10
                                        )
                                      : null,
                                    applySpecificCustomer:
                                      data.applySpecificCustomer,
                                    applyShopAll: data.applyShopAll,
                                    isAff: data.isAff,
                                    isRepeatable: data.isRepeatable,
                                    discountValue:
                                      data.discountType.toString() ===
                                      "SHIPPING"
                                        ? 100
                                        : decimal(data.value),
                                    discountValueType:
                                      data.discountType.toString() ===
                                      "SHIPPING"
                                        ? DiscountValueTypeEnum.PERCENTAGE
                                        : data.discountType,
                                    endDate: data.hasEndDate
                                      ? joinDateTime(data.endDate, data.endTime)
                                      : null,
                                    minAmountSpent:
                                      data.requirementsPicker !==
                                      RequirementsPicker.ORDER
                                        ? 0
                                        : parseFloat(data.minSpent),
                                    minCheckoutItemsQuantity:
                                      data.requirementsPicker !==
                                      RequirementsPicker.ITEM
                                        ? 0
                                        : parseFloat(
                                            data.minCheckoutItemsQuantity
                                          ),
                                    startDate: joinDateTime(
                                      data.startDate,
                                      data.startTime
                                    ),
                                    type:
                                      data.discountType.toString() ===
                                      "SHIPPING"
                                        ? VoucherTypeEnum.SHIPPING
                                        : data.type,
                                    usageLimit: data.hasUsageLimit
                                      ? parseInt(data.usageLimit, 10)
                                      : null,
                                    displayOrder: data.displayOrder,
                                    descriptionText: data.descriptionText,
                                    descriptionSubtext: data.descriptionSubtext,
                                    descriptionThirdtext:
                                      data.descriptionThirdtext,
                                    includeDiscountProduct:
                                      data.includeDiscountProduct,
                                    includeComboProduct:
                                      data.includeComboProduct
                                  }
                                }
                              });

                              return result.data.voucherUpdate.errors;
                            };

                            const {
                              loadNextPage,
                              loadPreviousPage,
                              pageInfo
                            } = paginate(tabPageInfo, paginationState, params);

                            const [
                              voucherLanguagesAdd,
                              voucherLanguagesAddOpts
                            ] = useVoucherLanguagesAddMutation({
                              onCompleted: data => {
                                if (
                                  data.voucherLanguagesAdd?.errors?.length === 0
                                ) {
                                  notify({
                                    status: "success",
                                    text: intl.formatMessage({
                                      defaultMessage:
                                        "Voucher Assign to Language"
                                    })
                                  });
                                  reset();
                                  closeModal();
                                }
                              }
                            });
                            const [
                              voucherLanguagesRemove
                            ] = useVoucherLanguagesRemoveMutation({
                              onCompleted: data => {
                                if (
                                  data.voucherLanguagesRemove?.errors
                                    ?.length === 0
                                ) {
                                  notify({
                                    status: "success",
                                    text: intl.formatMessage({
                                      defaultMessage:
                                        "Voucher Unassign to Language"
                                    })
                                  });
                                  reset();
                                }
                              }
                            });
                            return (
                              <>
                                <WindowTitle
                                  title={intl.formatMessage(
                                    sectionNames.vouchers
                                  )}
                                />
                                <VoucherDetailsPage
                                  defaultCurrency={maybe(
                                    () => shop.defaultCurrency,
                                    "USD"
                                  )}
                                  voucher={maybe(() => data.voucher)}
                                  disabled={
                                    loading ||
                                    voucherCataloguesRemoveOpts.loading
                                  }
                                  errors={
                                    voucherUpdateOpts.data?.voucherUpdate
                                      .errors || []
                                  }
                                  pageInfo={pageInfo}
                                  onNextPage={loadNextPage}
                                  onPreviousPage={loadPreviousPage}
                                  onLanguageAssign={() =>
                                    openModal("assign-language")
                                  }
                                  onLanguageUnAssign={languageId =>
                                    voucherLanguagesRemove({
                                      variables: {
                                        id,
                                        languages: [languageId]
                                      }
                                    })
                                  }
                                  onCategoryClick={id => () =>
                                    navigate(categoryUrl(id))}
                                  onCountryAssign={() =>
                                    openModal("assign-country")
                                  }
                                  onCountryUnassign={countryCode =>
                                    voucherUpdate({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          countries: data.voucher.countries
                                            .filter(
                                              country =>
                                                country.code !== countryCode
                                            )
                                            .map(country => country.code)
                                        }
                                      }
                                    })
                                  }
                                  onCollectionClick={id => () =>
                                    navigate(collectionUrl(id))}
                                  onProductClick={id => () =>
                                    navigate(productUrl(id))}
                                  onCategoryAssign={() =>
                                    openModal("assign-category")
                                  }
                                  onCollectionAssign={() =>
                                    openModal("assign-collection")
                                  }
                                  onProductAssign={() =>
                                    openModal("assign-product")
                                  }
                                  onVariantAssign={() =>
                                    openModal("assign-variant")
                                  }
                                  onExcludeCategoryAssign={() =>
                                    openModal("assign-exclude-category")
                                  }
                                  onExcludeCollectionAssign={() =>
                                    openModal("assign-exclude-collection")
                                  }
                                  onExcludeProductAssign={() =>
                                    openModal("assign-exclude-product")
                                  }
                                  onExcludeVariantAssign={() =>
                                    openModal("assign-exclude-variant")
                                  }
                                  onCollectionUnassign={collectionId =>
                                    handleCollectionsUnassign([collectionId])
                                  }
                                  onExcludeCollectionUnassign={collectionId =>
                                    handleExcludeCollectionsUnassign([
                                      collectionId
                                    ])
                                  }
                                  onCategoryUnassign={categoryId =>
                                    handleCategoriesUnassign([categoryId])
                                  }
                                  onExcludeCategoryUnassign={categoryId =>
                                    handleExcludeCategoriesUnassign([
                                      categoryId
                                    ])
                                  }
                                  onProductUnassign={productId =>
                                    handleProductsUnassign([productId])
                                  }
                                  onExcludeProductUnassign={productId =>
                                    handleExcludeProductsUnassign([productId])
                                  }
                                  onVariantUnassign={variantId =>
                                    handleVariantsUnassign([variantId])
                                  }
                                  onExcludeVariantUnassign={variantId =>
                                    handleExcludeVariantsUnassign([variantId])
                                  }
                                  onVariantClick={(
                                    productId,
                                    variantId
                                  ) => () =>
                                    navigate(
                                      productVariantEditUrl(
                                        productId,
                                        variantId
                                      )
                                    )}
                                  activeTab={params.activeTab}
                                  onBack={() => navigate(voucherListUrl())}
                                  onTabClick={changeTab}
                                  onSubmit={handleSubmit}
                                  onRemove={() => openModal("remove")}
                                  saveButtonBarState={voucherUpdateOpts.status}
                                  categoryListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-category", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign category from voucher, button"
                                        id="voucherDetailsUnassignCategory"
                                      />
                                    </Button>
                                  }
                                  collectionListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-collection", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign collection from voucher, button"
                                        id="voucherDetailsUnassignCollection"
                                      />
                                    </Button>
                                  }
                                  productListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-product", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign product from voucher, button"
                                        id="voucherDetailsUnassignProduct"
                                      />
                                    </Button>
                                  }
                                  variantListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-variant", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign product sku from sale, button"
                                        id="voucherDetailsUnassignVariant"
                                      />
                                    </Button>
                                  }
                                  excludeCategoryListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-exclude-category", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign category from voucher, button"
                                        id="voucherDetailsUnassignCategory"
                                      />
                                    </Button>
                                  }
                                  excludeCollectionListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal(
                                          "unassign-exclude-collection",
                                          {
                                            ids: listElements
                                          }
                                        )
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign collection from voucher, button"
                                        id="voucherDetailsUnassignCollection"
                                      />
                                    </Button>
                                  }
                                  excludeProductListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-exclude-product", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign product from voucher, button"
                                        id="voucherDetailsUnassignProduct"
                                      />
                                    </Button>
                                  }
                                  excludeVariantListToolbar={
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        openModal("unassign-exclude-variant", {
                                          ids: listElements
                                        })
                                      }
                                    >
                                      <FormattedMessage
                                        defaultMessage="Unassign"
                                        description="unassign product sku from sale, button"
                                        id="voucherDetailsUnassignVariant"
                                      />
                                    </Button>
                                  }
                                  isChecked={isSelected}
                                  selected={listElements.length}
                                  toggle={toggle}
                                  toggleAll={toggleAll}
                                />
                                <AssignCategoriesDialog
                                  categories={maybe(() =>
                                    searchCategoriesOpts.data.search.edges
                                      .map(edge => edge.node)
                                      .filter(
                                        suggestedCategory =>
                                          suggestedCategory.id
                                      )
                                  )}
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  hasMore={
                                    searchCategoriesOpts.data?.search.pageInfo
                                      .hasNextPage
                                  }
                                  open={params.action === "assign-category"}
                                  onFetch={searchCategories}
                                  onFetchMore={loadMoreCategories}
                                  loading={searchCategoriesOpts.loading}
                                  onClose={closeModal}
                                  onSubmit={categories =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          categories
                                        }
                                      }
                                    })
                                  }
                                />
                                <AssignCategoriesDialog
                                  categories={maybe(() =>
                                    searchCategoriesOpts.data.search.edges
                                      .map(edge => edge.node)
                                      .filter(
                                        suggestedCategory =>
                                          suggestedCategory.id
                                      )
                                  )}
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  hasMore={
                                    searchCategoriesOpts.data?.search.pageInfo
                                      .hasNextPage
                                  }
                                  open={
                                    params.action === "assign-exclude-category"
                                  }
                                  onFetch={searchCategories}
                                  onFetchMore={loadMoreCategories}
                                  loading={searchCategoriesOpts.loading}
                                  onClose={closeModal}
                                  onSubmit={categories =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          categories,
                                          exclude: true
                                        }
                                      }
                                    })
                                  }
                                />
                                <AssignLanguageDialog
                                  languages={maybe(() =>
                                    searchLanguagesOpts.data.search.edges.map(
                                      edge => edge.node
                                    )
                                  )}
                                  confirmButtonState={
                                    voucherLanguagesAddOpts.status
                                  }
                                  hasMore={
                                    searchLanguagesOpts.data?.search.pageInfo
                                      .hasNextPage
                                  }
                                  open={params.action === "assign-language"}
                                  onFetch={searchLanguages}
                                  onFetchMore={loadMoreLanguages}
                                  loading={searchLanguagesOpts.loading}
                                  onClose={closeModal}
                                  onSubmit={languages =>
                                    voucherLanguagesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        languages: languages.map(
                                          language => language.id
                                        )
                                      }
                                    })
                                  }
                                />
                                <AssignCollectionDialog
                                  collections={maybe(() =>
                                    searchCollectionsOpts.data.search.edges
                                      .map(edge => edge.node)
                                      .filter(
                                        suggestedCategory =>
                                          suggestedCategory.id
                                      )
                                  )}
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  hasMore={
                                    searchCollectionsOpts.data?.search.pageInfo
                                      .hasNextPage
                                  }
                                  open={params.action === "assign-collection"}
                                  onFetch={searchCollections}
                                  onFetchMore={loadMoreCollections}
                                  loading={searchCollectionsOpts.loading}
                                  onClose={closeModal}
                                  onSubmit={collections =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          collections
                                        }
                                      }
                                    })
                                  }
                                />
                                <AssignCollectionDialog
                                  collections={maybe(() =>
                                    searchCollectionsOpts.data.search.edges
                                      .map(edge => edge.node)
                                      .filter(
                                        suggestedCategory =>
                                          suggestedCategory.id
                                      )
                                  )}
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  hasMore={
                                    searchCollectionsOpts.data?.search.pageInfo
                                      .hasNextPage
                                  }
                                  open={
                                    params.action ===
                                    "assign-exclude-collection"
                                  }
                                  onFetch={searchCollections}
                                  onFetchMore={loadMoreCollections}
                                  loading={searchCollectionsOpts.loading}
                                  onClose={closeModal}
                                  onSubmit={collections =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          collections,
                                          exclude: true
                                        }
                                      }
                                    })
                                  }
                                />
                                <DiscountCountrySelectDialog
                                  confirmButtonState={voucherUpdateOpts.status}
                                  countries={maybe(() => shop.allCountries, [])}
                                  onClose={() => navigate(voucherUrl(id))}
                                  onConfirm={formData =>
                                    voucherUpdate({
                                      variables: {
                                        id,
                                        input: {
                                          countries: formData.countries
                                        }
                                      }
                                    })
                                  }
                                  open={params.action === "assign-country"}
                                  initial={maybe(
                                    () =>
                                      data.voucher.countries.map(
                                        country => country.code
                                      ),
                                    []
                                  )}
                                />
                                <AssignVariantDialog
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  confirmButtonState2={
                                    voucherCataloguesReverseElectionOpts.status
                                  }
                                  hasMore={
                                    searchProductVariantsOpts.data?.search
                                      ?.pageInfo.hasNextPage
                                  }
                                  open={params.action === "assign-variant"}
                                  onFetch={searchProductVariants}
                                  onFetchMore={loadMoreProductVariants}
                                  loading={searchProductVariantsOpts.loading}
                                  onClose={closeModal}
                                  onSubmit2={variants =>
                                    voucherCataloguesReverseElection({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          variants: variants.map(
                                            variant => variant.id
                                          )
                                        }
                                      }
                                    })
                                  }
                                  onSubmit={variants =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          variants: variants.map(
                                            variant => variant.id
                                          )
                                        }
                                      }
                                    })
                                  }
                                  variants={mapEdgesToItems(
                                    searchProductVariantsOpts?.data?.search
                                  )?.filter(
                                    suggestedVariant => suggestedVariant.id
                                  )}
                                />
                                <AssignVariantDialog
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  confirmButtonState2={
                                    voucherCataloguesReverseElectionOpts.status
                                  }
                                  hasMore={
                                    searchProductVariantsOpts.data?.search
                                      ?.pageInfo.hasNextPage
                                  }
                                  open={
                                    params.action === "assign-exclude-variant"
                                  }
                                  onFetch={searchProductVariants}
                                  onFetchMore={loadMoreProductVariants}
                                  loading={searchProductVariantsOpts.loading}
                                  onClose={closeModal}
                                  onSubmit2={variants =>
                                    voucherCataloguesReverseElection({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          variants: variants.map(
                                            variant => variant.id
                                          ),
                                          exclude: true
                                        }
                                      }
                                    })
                                  }
                                  onSubmit={variants =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          variants: variants.map(
                                            variant => variant.id
                                          ),
                                          exclude: true
                                        }
                                      }
                                    })
                                  }
                                  variants={mapEdgesToItems(
                                    searchProductVariantsOpts?.data?.search
                                  )?.filter(
                                    suggestedVariant => suggestedVariant.id
                                  )}
                                />
                                <AssignProductDialog
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  confirmButtonState2={
                                    voucherCataloguesReverseElectionOpts.status
                                  }
                                  hasMore={
                                    searchProductsOpts.data?.search?.pageInfo
                                      .hasNextPage
                                  }
                                  open={params.action === "assign-product"}
                                  onFetch={searchProducts}
                                  onFetchMore={loadMoreProducts}
                                  loading={searchProductsOpts.loading}
                                  onClose={closeModal}
                                  onSubmit2={products =>
                                    voucherCataloguesReverseElection({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          products: products.map(
                                            product => product.id
                                          )
                                        }
                                      }
                                    })
                                  }
                                  onSubmit={products =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          products: products.map(
                                            product => product.id
                                          )
                                        }
                                      }
                                    })
                                  }
                                  products={mapEdgesToItems(
                                    searchProductsOpts?.data?.search
                                  )?.filter(
                                    suggestedProduct => suggestedProduct.id
                                  )}
                                />
                                <AssignProductDialog
                                  confirmButtonState={
                                    voucherCataloguesAddOpts.status
                                  }
                                  confirmButtonState2={
                                    voucherCataloguesReverseElectionOpts.status
                                  }
                                  hasMore={
                                    searchProductsOpts.data?.search?.pageInfo
                                      .hasNextPage
                                  }
                                  open={
                                    params.action === "assign-exclude-product"
                                  }
                                  onFetch={searchProducts}
                                  onFetchMore={loadMoreProducts}
                                  loading={searchProductsOpts.loading}
                                  onClose={closeModal}
                                  onSubmit2={products =>
                                    voucherCataloguesReverseElection({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          products: products.map(
                                            product => product.id
                                          ),
                                          exclude: true
                                        }
                                      }
                                    })
                                  }
                                  onSubmit={products =>
                                    voucherCataloguesAdd({
                                      variables: {
                                        ...paginationState,
                                        id,
                                        input: {
                                          products: products.map(
                                            product => product.id
                                          ),
                                          exclude: true
                                        }
                                      }
                                    })
                                  }
                                  products={mapEdgesToItems(
                                    searchProductsOpts?.data?.search
                                  )?.filter(
                                    suggestedProduct => suggestedProduct.id
                                  )}
                                />
                                <ActionDialog
                                  open={
                                    params.action === "unassign-category" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Categories From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleCategoriesUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this category?} other{Are you sure you want to unassign {displayQuantity} categories?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>
                                <ActionDialog
                                  open={
                                    params.action === "unassign-collection" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Collections From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleCollectionsUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this collection?} other{Are you sure you want to unassign {displayQuantity} collections?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>
                                <ActionDialog
                                  open={
                                    params.action === "unassign-product" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Products From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleProductsUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>
                                <ActionDialog
                                  open={
                                    params.action === "unassign-variant" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Variant From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleVariantsUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this variant?} other{Are you sure you want to unassign {displayQuantity} variants?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>

                                <ActionDialog
                                  open={
                                    params.action ===
                                      "unassign-exclude-category" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Categories From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleExcludeCategoriesUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this category?} other{Are you sure you want to unassign {displayQuantity} categories?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>
                                <ActionDialog
                                  open={
                                    params.action ===
                                      "unassign-exclude-collection" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Collections From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleExcludeCollectionsUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this collection?} other{Are you sure you want to unassign {displayQuantity} collections?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>
                                <ActionDialog
                                  open={
                                    params.action ===
                                      "unassign-exclude-product" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Products From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleExcludeProductsUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this product?} other{Are you sure you want to unassign {displayQuantity} products?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>
                                <ActionDialog
                                  open={
                                    params.action ===
                                      "unassign-exclude-variant" &&
                                    canOpenBulkActionDialog
                                  }
                                  title={intl.formatMessage({
                                    defaultMessage:
                                      "Unassign Variant From Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={
                                    voucherCataloguesRemoveOpts.status
                                  }
                                  onClose={closeModal}
                                  onConfirm={() =>
                                    handleExcludeVariantsUnassign(params.ids)
                                  }
                                >
                                  {canOpenBulkActionDialog && (
                                    <DialogContentText>
                                      <FormattedMessage
                                        defaultMessage="{counter,plural,one{Are you sure you want to unassign this variant?} other{Are you sure you want to unassign {displayQuantity} variants?}}"
                                        description="dialog content"
                                        values={{
                                          counter: params.ids.length,
                                          displayQuantity: (
                                            <strong>{params.ids.length}</strong>
                                          )
                                        }}
                                      />
                                    </DialogContentText>
                                  )}
                                </ActionDialog>

                                <ActionDialog
                                  open={params.action === "remove"}
                                  title={intl.formatMessage({
                                    defaultMessage: "Delete Coupon Code",
                                    description: "dialog header"
                                  })}
                                  confirmButtonState={voucherDeleteOpts.status}
                                  onClose={closeModal}
                                  variant="delete"
                                  onConfirm={() =>
                                    voucherDelete({
                                      variables: { id }
                                    })
                                  }
                                >
                                  <DialogContentText>
                                    <FormattedMessage
                                      defaultMessage="Are you sure you want to delete {voucherCode}?"
                                      description="dialog content"
                                      values={{
                                        voucherCode: (
                                          <strong>
                                            {maybe(
                                              () => data.voucher.code,
                                              "..."
                                            )}
                                          </strong>
                                        )
                                      }}
                                    />
                                  </DialogContentText>
                                </ActionDialog>
                              </>
                            );
                          }}
                        </TypedVoucherDetails>
                      )}
                    </TypedVoucherDelete>
                  )}
                </TypedVoucherUpdate>
              )}
            </TypedVoucherCataloguesReverseElection>
          )}
        </TypedVoucherCataloguesAdd>
      )}
    </TypedVoucherCataloguesRemove>
  );
};
export default VoucherDetails;
